import React, { useEffect, useRef, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import axios from 'axios';
import QrScanner from 'qr-scanner';
import { Camera, X, Check, AlertCircle, Keyboard, Loader, RefreshCw } from 'lucide-react';
import { useTranslation } from 'react-i18next';

/**
 * Reusable QR scanner that drives /api/packages/scan.
 *
 * The scanner is the single button every dashboard exposes for
 * package handoffs — courier, central staff, lab manager, store
 * manager. The server figures out the next legal transition from
 * the package's current status and the scanner's role, so this
 * component is intentionally dumb: it captures a code, sends it,
 * and reports the result.
 *
 * Props:
 *   onSuccess(payload) — called with { package, prevStatus, newStatus }
 *                        whenever a scan advances a package
 *   helpText           — short string explaining what the operator
 *                        should be scanning ("Scan the package QR
 *                        when you pick it up at the store")
 *   buttonLabel        — overrides the trigger button label
 *
 * Honors a manual-entry fallback for phones without a working camera
 * or a desktop without a webcam.
 */

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(8px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.625rem;
`;

const TriggerRow = styled.div`
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
`;

// Scanning is the primary action on every screen this component appears on, so
// it gets the one solid button and Manual entry recedes to a ghost. Two buttons
// of similar weight made the eye price them against each other; a keyboard is
// the fallback for when the camera will not cooperate, not an equal choice.
//
// Every token carries a literal fallback on purpose. `DesignTokens` is a
// createGlobalStyle mounted inside CentralStoreDashboard, so the custom
// properties only exist while THAT screen is rendered — this component also
// renders on the Courier, Lab and Manager dashboards, where `var(--accent)`
// would resolve to nothing. The fallback is the token's real value, so all five
// mount points look identical and the Central one picks up any future retheme.
const ScanButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.6rem 1rem;
  border-radius: 10px;
  border: 1px solid transparent;
  background: var(--accent, #5b8def);
  color: #ffffff;
  font-weight: 700;
  cursor: pointer;
  font-size: 0.875rem;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.08);
  transition: background var(--dur-hover, 160ms) var(--ease-out, cubic-bezier(0.23, 1, 0.32, 1)),
              box-shadow var(--dur-hover, 160ms) var(--ease-out, cubic-bezier(0.23, 1, 0.32, 1));

  &:hover:not(:disabled) {
    background: var(--accent-hover, #4574d6);
    box-shadow: 0 2px 6px rgba(91, 141, 239, 0.28);
  }
  /* A press should read as pressed, not as a lift. */
  &:active:not(:disabled) { box-shadow: inset 0 1px 2px rgba(16, 24, 40, 0.18); }
  &:focus-visible {
    outline: 2px solid var(--accent, #5b8def);
    outline-offset: 2px;
  }
  &:disabled { opacity: 0.55; cursor: not-allowed; box-shadow: none; }

  @media (prefers-reduced-motion: reduce) { transition: none; }
`;

// Ghost: no fill, no border until it is wanted. It still reaches AA on the
// surface it sits on, and keeps a visible focus ring for keyboard users.
const SecondaryButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.55rem 0.85rem;
  border-radius: 10px;
  border: 1px solid transparent;
  background: transparent;
  color: var(--ink-soft, #5a5a66);
  font-weight: 600;
  font-size: 0.85rem;
  cursor: pointer;
  transition: background var(--dur-hover, 160ms) var(--ease-out, cubic-bezier(0.23, 1, 0.32, 1)),
              color var(--dur-hover, 160ms) var(--ease-out, cubic-bezier(0.23, 1, 0.32, 1));

  &:hover:not(:disabled) {
    background: var(--accent-soft, #eef2ff);
    color: var(--accent-ink, #1d4ed8);
  }
  &:focus-visible {
    outline: 2px solid var(--accent, #5b8def);
    outline-offset: 2px;
  }
  &:disabled { opacity: 0.55; cursor: not-allowed; }

  @media (prefers-reduced-motion: reduce) { transition: none; }
`;

const Help = styled.div`
  font-size: 0.8rem;
  color: #64748b;
`;

const ScannerSurface = styled.div`
  position: relative;
  width: 100%;
  max-width: 480px;
  aspect-ratio: 1 / 1;
  background: #0f172a;
  border-radius: 16px;
  overflow: hidden;
  animation: ${fadeIn} 0.2s ease;

  video {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const ScannerOverlay = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: 0.75rem;
  pointer-events: none;
`;

const ScannerHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 0.5rem;

  .label {
    background: rgba(15, 23, 42, 0.6);
    color: white;
    padding: 0.3rem 0.6rem;
    border-radius: 8px;
    font-size: 0.75rem;
    font-weight: 600;
  }
`;

const CloseBtn = styled.button`
  pointer-events: auto;
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  background: rgba(15, 23, 42, 0.6);
  color: white;
  border: 1px solid rgba(255,255,255,0.2);
  border-radius: 8px;
  padding: 0.3rem 0.55rem;
  cursor: pointer;
  font-size: 0.75rem;
  font-weight: 600;
`;

const ManualForm = styled.form`
  display: flex;
  gap: 0.5rem;
  align-items: stretch;
  margin-top: 0.25rem;

  input {
    flex: 1;
    padding: 0.55rem 0.75rem;
    border: 1px solid #cbd5e1;
    border-radius: 10px;
    font-size: 0.9rem;
    text-transform: uppercase;
    font-family: inherit;
  }
`;

const Notice = styled.div`
  background: ${(p) =>
    p.kind === 'success' ? '#dcfce7' :
    p.kind === 'error'   ? '#fee2e2' :
    '#dbeafe'};
  color: ${(p) =>
    p.kind === 'success' ? '#166534' :
    p.kind === 'error'   ? '#991b1b' :
    '#1e40af'};
  border: 1px solid ${(p) =>
    p.kind === 'success' ? '#bbf7d0' :
    p.kind === 'error'   ? '#fecaca' :
    '#bfdbfe'};
  border-radius: 12px;
  padding: 0.625rem 0.875rem;
  font-size: 0.85rem;
  display: flex;
  gap: 0.5rem;
  align-items: flex-start;
  animation: ${fadeIn} 0.2s ease;
`;

const PackageScanner = ({
  onSuccess,
  helpText,
  buttonLabel,
  endpoint = '/api/packages/scan',
  placeholder = 'PKG-000123',
  formatSuccess,
}) => {
  const { t } = useTranslation();
  const triggerLabel = buttonLabel || t('packageScanner.scanPackage');
  const [isScanning, setIsScanning] = useState(false);
  const [showManual, setShowManual] = useState(false);
  const [manualCode, setManualCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [notice, setNotice] = useState(null); // { kind, message }
  const videoRef = useRef(null);
  const qrScannerRef = useRef(null);
  const streamRef = useRef(null);
  const fallbackScanTimerRef = useRef(null);

  const normalizeCode = (value) => {
    const raw = String(value || '').trim();
    if (!raw) return '';
    try {
      const json = JSON.parse(raw);
      if (json?.packageNumber) return String(json.packageNumber).trim();
      if (json?.qrCode) return String(json.qrCode).trim();
      if (json?.code) return String(json.code).trim();
    } catch (_) {}
    try {
      const url = new URL(raw, window.location.origin);
      const fromQuery = url.searchParams.get('package') || url.searchParams.get('packageNumber') || url.searchParams.get('code');
      if (fromQuery) return fromQuery.trim();
      const pathMatch = decodeURIComponent(url.pathname).match(/PKG-[A-Z0-9-]+/i);
      if (pathMatch) return pathMatch[0].trim();
    } catch (_) {}
    const inlineMatch = raw.match(/PKG-[A-Z0-9-]+(?:\|[^\s]+)*/i);
    return (inlineMatch ? inlineMatch[0] : raw).trim();
  };

  const processScannedText = (value) => {
    const code = normalizeCode(value);
    if (!code) return false;
    stopScanner();
    submitCode(code);
    return true;
  };

  const startFallbackFrameScanner = () => {
    if (fallbackScanTimerRef.current) clearInterval(fallbackScanTimerRef.current);
    fallbackScanTimerRef.current = window.setInterval(async () => {
      const video = videoRef.current;
      if (!video || video.readyState < 2) return;
      try {
        const result = await QrScanner.scanImage(video, {
          returnDetailedScanResult: true,
          alsoTryWithoutScanRegion: true,
        });
        const text = typeof result === 'string' ? result : result?.data;
        if (text) processScannedText(text);
      } catch (_) {
        // No QR in this frame; keep scanning.
      }
    }, 350);
  };

  const startScanner = async () => {
    setNotice(null);
    setIsScanning(true);
    // Wait for the video element to mount on the next tick.
    let retries = 0;
    while (!videoRef.current && retries < 10) {
      await new Promise((r) => setTimeout(r, 100));
      retries += 1;
    }
    if (!videoRef.current) {
      setIsScanning(false);
      setNotice({ kind: 'error', message: 'Video element not available. Try the manual entry.' });
      return;
    }
    try {
      videoRef.current.style.display = 'block';
      videoRef.current.style.visibility = 'visible';
      videoRef.current.style.width = '100%';
      videoRef.current.style.height = '100%';
      videoRef.current.style.objectFit = 'cover';
      videoRef.current.setAttribute('playsinline', 'true');
      videoRef.current.setAttribute('muted', 'true');
      videoRef.current.setAttribute('autoplay', 'true');

      qrScannerRef.current = new QrScanner(
        videoRef.current,
        (result) => {
          const code = typeof result === 'string' ? result : result?.data;
          if (code) processScannedText(code);
        },
        {
          returnDetailedScanResult: true,
          highlightScanRegion: true,
          highlightCodeOutline: true,
          maxScansPerSecond: 12,
          preferredCamera: 'environment',
          calculateScanRegion: (video) => {
            const smallerDimension = Math.min(video.videoWidth || video.clientWidth, video.videoHeight || video.clientHeight);
            const scanRegionSize = Math.round(smallerDimension * 0.86);
            return {
              x: Math.round(((video.videoWidth || video.clientWidth) - scanRegionSize) / 2),
              y: Math.round(((video.videoHeight || video.clientHeight) - scanRegionSize) / 2),
              width: scanRegionSize,
              height: scanRegionSize,
              downScaledWidth: 560,
              downScaledHeight: 560,
            };
          },
        }
      );
      await qrScannerRef.current.start();
      if (videoRef.current?.srcObject) {
        streamRef.current = videoRef.current.srcObject;
      }
      startFallbackFrameScanner();
    } catch (err) {
      console.error('Scanner error:', err);
      setIsScanning(false);
      setNotice({
        kind: 'error',
        message: err?.name === 'NotAllowedError'
          ? 'Camera permission denied. Use manual entry.'
          : 'Could not start camera. Use manual entry.',
      });
    }
  };

  const stopScanner = () => {
    if (fallbackScanTimerRef.current) {
      clearInterval(fallbackScanTimerRef.current);
      fallbackScanTimerRef.current = null;
    }
    if (qrScannerRef.current) {
      try { qrScannerRef.current.stop(); } catch (_) { /* ignore */ }
      try { qrScannerRef.current.destroy(); } catch (_) { /* ignore */ }
      qrScannerRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    setIsScanning(false);
  };

  // Tear down on unmount.
  useEffect(() => () => stopScanner(), []);

  const submitCode = async (code) => {
    code = normalizeCode(code);
    if (!code) return;
    setSubmitting(true);
    setNotice(null);
    try {
      const token = localStorage.getItem('token');
      const resp = await axios.post(
        endpoint,
        { code },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const payload = resp.data;
      const message = typeof formatSuccess === 'function'
        ? formatSuccess(payload, code)
        : (() => {
            const newStatus = payload?.newStatus || payload?.package?.status || 'updated';
            return `Package ${payload?.package?.packageNumber || code} → ${newStatus.replace(/_/g, ' ')}.`;
          })();
      setNotice({ kind: 'success', message });
      setManualCode('');
      setShowManual(false);
      if (typeof onSuccess === 'function') {
        try { onSuccess(payload); } catch (_) { /* swallow caller errors */ }
      }
    } catch (err) {
      const message = err.response?.data?.message || err.message || t('packageScanner.scanFailed');
      setNotice({ kind: 'error', message });
    } finally {
      setSubmitting(false);
    }
  };

  const handleManualSubmit = (e) => {
    e.preventDefault();
    const code = manualCode.trim();
    if (code) submitCode(code);
  };

  return (
    <Wrap>
      {helpText && <Help>{helpText}</Help>}

      {isScanning ? (
        <ScannerSurface>
          <video ref={videoRef} playsInline muted />
          <ScannerOverlay>
            <ScannerHeader>
              <span className="label">{t('packageScanner.scanning')}</span>
              <CloseBtn type="button" onClick={stopScanner}>
                <X size={14} /> {t('packageScanner.cancel')}
              </CloseBtn>
            </ScannerHeader>
            <span />
          </ScannerOverlay>
        </ScannerSurface>
      ) : (
        <TriggerRow>
          <ScanButton type="button" onClick={startScanner} disabled={submitting}>
            {submitting ? <Loader size={15} /> : <Camera size={15} />}
            {submitting ? t('packageScanner.submitting') : triggerLabel}
          </ScanButton>
          <SecondaryButton type="button" onClick={() => { setShowManual((v) => !v); setNotice(null); }}>
            <Keyboard size={15} /> {t('packageScanner.manualEntry')}
          </SecondaryButton>
        </TriggerRow>
      )}

      {showManual && !isScanning && (
        <ManualForm onSubmit={handleManualSubmit}>
          <input
            type="text"
            value={manualCode}
            onChange={(e) => setManualCode(e.target.value)}
            placeholder={placeholder}
            disabled={submitting}
          />
          <ScanButton type="submit" disabled={submitting || !manualCode.trim()}>
            {submitting ? <RefreshCw size={15} /> : <Check size={15} />}
            {t('packageScanner.submit')}
          </ScanButton>
        </ManualForm>
      )}

      {notice && (
        <Notice kind={notice.kind}>
          {notice.kind === 'error' ? <AlertCircle size={16} /> :
           notice.kind === 'success' ? <Check size={16} /> :
           <AlertCircle size={16} />}
          <span>{notice.message}</span>
        </Notice>
      )}
    </Wrap>
  );
};

export default PackageScanner;
